import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type SwitchProps = {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  /** Accessible name when the switch has no visible label (`SwitchField` names it). */
  'aria-label'?: string;
  className?: string;
};

/** On/off toggle (WAI-ARIA switch). */
export function Switch({
  checked,
  onCheckedChange,
  disabled,
  id,
  'aria-label': ariaLabel,
  className,
}: SwitchProps) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-primary' : 'bg-muted-foreground/30',
        className
      )}
    >
      <SwitchThumb checked={checked} />
    </button>
  );
}

function SwitchThumb({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'pointer-events-none absolute top-0.5 h-4 w-4 rounded-full bg-background shadow-sm transition-transform',
        checked ? 'translate-x-4' : 'translate-x-0.5'
      )}
    />
  );
}

export type SwitchFieldProps = Omit<SwitchProps, 'aria-label'> & {
  label: ReactNode;
  description?: ReactNode;
};

/** A whole clickable row: label, optional description and the switch. */
export function SwitchField({
  checked,
  onCheckedChange,
  disabled,
  id,
  label,
  description,
  className,
}: SwitchFieldProps) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'border-primary/30 bg-primary/10' : 'border-border bg-muted/60 hover:bg-muted',
        className
      )}
    >
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className={cn('font-medium', checked ? 'text-primary' : 'text-foreground')}>
          {label}
        </span>
        {description && <span className="text-xs text-muted-foreground">{description}</span>}
      </span>
      <span
        aria-hidden
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
          checked ? 'bg-primary' : 'bg-muted-foreground/30'
        )}
      >
        <SwitchThumb checked={checked} />
      </span>
    </button>
  );
}
